import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useHistory } from "react-router-dom";
import styled from "styled-components";
import {
  Restaurant,
  Store,
  LocalHospital,
  Fastfood,
  ShoppingBasket,
  LocalPharmacy,
  LocalOffer,
  LocalShipping,
  Storefront,
  Business,
} from "@material-ui/icons";
import { globalSearch, clearSearch } from "../redux/globalSearchSlice";
import Navbar from "../components/Navbar";
import { SEOHead } from "../components/SEO/SEOHead";
import { search as trackSearch } from "../utils/analytics";
// import Footer from "../components/Footer";
import { mobile } from "../responsive";
import { buildVendorParam } from "../utils/vendorSlug";
import { productPathUnder } from "../utils/seoPaths";
import CrawlLink from "../components/CrawlLink";
import { COUNTRY_NAME, formatCurrency } from "../config/market";
import DeliveryBadge from "../components/DeliveryBadge";
import useCoverageBatch from "../redux/useCoverageBatch";
import {
  PRICE_ON_REQUEST_LABEL,
  isQuoteOnly,
  publishedPrice,
} from "../utils/priceOnRequest";

const Container = styled.div`
  min-height: 100vh;
  background-color: #f5f5f5;
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  /* Offset for the fixed Navbar.
     Desktop: ~121px (95 logo + 10 mb + 16 wrapper padding).
     Mobile (≤850px): stacked logo+auth row + search row ≈ 130px. */
  padding: var(--lusuku-nav-h, 125px) 20px 40px;
  ${mobile({ padding: "var(--lusuku-nav-h, 132px) 12px 30px" })}
`;

const SearchHeader = styled.div`
  margin-bottom: 30px;
`;

const SearchTitle = styled.h1`
  font-size: 24px;
  color: #333;
  margin-bottom: 10px;
  ${mobile({ fontSize: "20px" })}
`;

const ResultCount = styled.p`
  color: #666;
  font-size: 14px;
`;

const SearchHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
`;

const ClearSearchButton = styled.button`
  padding: 8px 18px;
  background-color: #d00000ff;
  color: #e0e0e0;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background-color: #d0d0d0;
    color: #333;
  }
`;

const CategoryTabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 30px;
  border-bottom: 1px solid #ddd;
  padding-bottom: 15px;
`;

const CategoryTab = styled.button`
  padding: 8px 16px;
  border: none;
  background-color: ${(props) => (props.active ? "#83B524" : "#e0e0e0")};
  color: ${(props) => (props.active ? "white" : "#333")};
  border-radius: 20px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${(props) => (props.active ? "#699B07" : "#d0d0d0")};
  }

  span {
    margin-left: 5px;
    font-size: 12px;
    opacity: 0.8;
  }
`;

const ResultsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  ${mobile({ gridTemplateColumns: "1fr" })}
`;

const ResultCard = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  }
`;

// Result types whose image is a LOGO (vendor profiles) rather than a product photo —
// these render "contain" so the mark is never cropped.
const LOGO_RESULT_TYPES = ["brand", "restaurant", "retailer", "healthcare"];

const CardImage = styled.div`
  width: 100%;
  height: 160px;
  box-sizing: border-box;
  /* Brand/vendor images are LOGOS (often round or padded squares) — cropping them
     with cover "cuts out" the mark. Render those with contain + padding so the whole
     logo shows. Product photos keep cover so they fill the frame. */
  background-color: ${(props) => (props.$contain ? "#f4f6f8" : "#f0f0f0")};
  background-image: ${(props) => (props.src ? `url(${props.src})` : "none")};
  background-size: ${(props) => (props.$contain ? "contain" : "cover")};
  background-repeat: no-repeat;
  background-position: center;
  ${(props) => (props.$contain ? "padding: 16px;" : "")}
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
`;

const PlaceholderSurface = styled.div`
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 18% 22%, rgba(255, 255, 255, 0.14) 0%, transparent 42%),
    radial-gradient(circle at 82% 14%, rgba(255, 193, 7, 0.18) 0%, transparent 46%),
    linear-gradient(142deg, #17344f 0%, #1f4f6e 42%, #0f2d43 100%);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PlaceholderIconWrap = styled.div`
  position: relative;
  z-index: 1;
  width: 72px;
  height: 72px;
  border-radius: 18px;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.08));
  border: 1px solid rgba(255, 255, 255, 0.32);
  box-shadow:
    0 12px 24px rgba(0, 0, 0, 0.28),
    inset 0 1px 0 rgba(255, 255, 255, 0.5),
    inset 0 -2px 8px rgba(0, 0, 0, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(2px);

  svg {
    font-size: 38px;
    color: rgba(255, 255, 255, 0.92);
  }
`;

const CardContent = styled.div`
  padding: 15px;
`;

const CardType = styled.span`
  display: inline-block;
  padding: 4px 8px;
  background-color: ${(props) => {
    switch (props.type) {
      case "restaurant":
        return "#FF6B6B";
      case "retailer":
        return "#4ECDC4";
      case "healthcare":
        return "#E91E63";
      case "restaurantProduct":
        return "#FF8E53";
      case "retailerProduct":
        return "#45B7D1";
      case "healthcareProduct":
        return "#9C27B0";
      case "brand":
        return "#324D54";
      case "brandProduct":
        return "#C9A227";
      case "deal":
        return "#FFD93D";
      case "wholesaleProduct":
        return "#6BCB77";
      case "generalListing":
        return "#8B8000";
      default:
        return "#999";
    }
  }};
  color: white;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  border-radius: 4px;
  margin-bottom: 8px;
`;

const CardTitle = styled.h3`
  font-size: 16px;
  color: #333;
  margin: 0 0 5px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardSubtitle = styled.p`
  font-size: 13px;
  color: #666;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardPrice = styled.p`
  font-size: 16px;
  font-weight: 600;
  color: #83B524;
  margin: 8px 0 0 0;
`;

/* The delivery verdict under a result. The badge has no action of its own, so
   a tap on it opens the shop like the rest of the card. */
const CardBadgeRow = styled.div`
  margin-top: 8px;
  display: flex;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #83B524;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const NoResults = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;

  h3 {
    font-size: 20px;
    margin-bottom: 10px;
    color: #333;
  }

  p {
    font-size: 14px;
  }
`;

const getTypeLabel = (type) => {
  const labels = {
    restaurant: "Restaurant",
    retailer: "Retailer",
    healthcare: "Healthcare",
    restaurantProduct: "Food",
    retailerProduct: "Product",
    healthcareProduct: "Health Product",
    brand: "Brand",
    brandProduct: "Brand Product",
    deal: "Deal",
    wholesaleProduct: "Wholesale",
    generalListing: "General Listing",
  };
  return labels[type] || type;
};

const getPlaceholderIcon = (type) => {
  const icons = {
    restaurant: Restaurant,
    retailer: Store,
    healthcare: LocalHospital,
    restaurantProduct: Fastfood,
    retailerProduct: ShoppingBasket,
    healthcareProduct: LocalPharmacy,
    brand: Business,
    brandProduct: Storefront,
    deal: LocalOffer,
    wholesaleProduct: LocalShipping,
    generalListing: Storefront,
  };
  return icons[type] || Storefront;
};

/**
 * Where a search result leads, and which vendor delivers it.
 *
 * One answer for both questions, so the card can never show the delivery
 * verdict of a different shop than the one a tap opens. `vendorId` is the id
 * the shop page itself checks coverage with (the same id in its URL), or null
 * for results that do not belong to a single delivering vendor (deals,
 * wholesale and general listings).
 */
const resultTargetOf = (item) => {
  switch (item.type) {
    case "restaurant":
      return { path: `/restaurant/shop/${buildVendorParam(item.name, item._id)}`, vendorId: item._id };
    case "retailer":
      return { path: `/shop/${buildVendorParam(item.name, item._id)}`, vendorId: item._id };
    case "healthcare":
      return { path: `/healthcare/shop/${buildVendorParam(item.name, item._id)}`, vendorId: item._id };
    case "brand": {
      const brandId = item.navigateParams?.brandId || item._id;
      return {
        path: `/brand/shop/${buildVendorParam(item.tradingName || item.name, brandId)}`,
        vendorId: brandId,
      };
    }
    case "restaurantProduct": {
      const id =
        item.restaurantId?._id ||
        item.navigateParams?.restaurantId ||
        item.navigateParams?.vendorId;
      return id ? { path: `/restaurant/shop/${id}`, vendorId: id } : null;
    }
    /* A product result opens the product's own page under its storefront
       (2 Nov 2026), not the storefront, where the item searched for was
       nowhere in sight. The storefront stays the fallback for a result
       without a product id. */
    case "retailerProduct": {
      const id = item.retailerWholesalerId?._id || item.navigateParams?.retailerId;
      if (!id) return null;
      const shop = `/shop/${buildVendorParam(item.retailerWholesalerId?.name || "", id)}`;
      return { path: productPathUnder(shop, item) || shop, vendorId: id };
    }
    case "healthcareProduct": {
      const id = item.healthcareId?._id || item.navigateParams?.healthcareId || item.navigateParams?.vendorId;
      if (!id) return null;
      const shop = `/healthcare/shop/${buildVendorParam(item.healthcareId?.name || "", id)}`;
      return { path: productPathUnder(shop, item) || shop, vendorId: id };
    }
    case "brandProduct": {
      const id = item.brandId?._id || item.navigateParams?.brandId || item.brandId;
      if (!id) return null;
      const shop = `/brand/shop/${buildVendorParam(item.brandId?.tradingName || item.brandId?.name || "", id)}`;
      return { path: productPathUnder(shop, item) || shop, vendorId: id };
    }
    case "deal":
      return { path: `/promoProduct/${item._id}`, vendorId: null };
    case "wholesaleProduct":
      return { path: item._id ? `/product/${item._id}` : "/shop", vendorId: null };
    case "generalListing":
      return item._id ? { path: `/product/${item._id}`, vendorId: null } : null;
    default:
      return null;
  }
};

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const SearchResultsPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const query = useQuery();
  const searchQuery = query.get("q") || "";

  const { results, allResults, totalCount, isSearching, hasSearched } =
    useSelector((state) => state.globalSearch);

  const [activeCategory, setActiveCategory] = useState("all");

  useEffect(() => {
    if (searchQuery) {
      dispatch(globalSearch(searchQuery));
    } else {
      dispatch(clearSearch());
    }
  }, [searchQuery, dispatch]);

  useEffect(() => {
    if (hasSearched && searchQuery) {
      trackSearch({ searchTerm: searchQuery, resultCount: totalCount });
    }
  }, [hasSearched, searchQuery, totalCount]);

  const categories = [
    { key: "all", label: "All", count: totalCount },
    { key: "restaurants", label: "Restaurants", count: results.restaurants?.length || 0 },
    { key: "retailers", label: "Retailers", count: results.retailers?.length || 0 },
    { key: "healthcare", label: "Healthcare", count: results.healthcare?.length || 0 },
    { key: "brands", label: "Brands", count: results.brands?.length || 0 },
    { key: "restaurantProducts", label: "Food", count: results.restaurantProducts?.length || 0 },
    { key: "retailerProducts", label: "Products", count: results.retailerProducts?.length || 0 },
    { key: "healthcareProducts", label: "Health Products", count: results.healthcareProducts?.length || 0 },
    { key: "brandProducts", label: "Brand Products", count: results.brandProducts?.length || 0 },
    { key: "deals", label: "Deals", count: results.deals?.length || 0 },
    { key: "wholesaleProducts", label: "Wholesale", count: results.wholesaleProducts?.length || 0 },
    { key: "generalListings", label: "General Listings", count: results.generalListings?.length || 0 },
  ].filter((cat) => cat.key === "all" || cat.count > 0);

  const getFilteredResults = () => {
    if (activeCategory === "all") {
      return allResults;
    }
    return results[activeCategory] || [];
  };

  const handleResultClick = (item) => {
    const target = resultTargetOf(item);
    if (target?.path) history.push(target.path);
  };

  /**
   * WHAT ONE RESULT'S PRICE ROW SAYS, or null when it should have none.
   *
   * WAS `{item.price && <CardPrice>...}`, which rendered NOTHING for a
   * quote-only result. A card with no price element at all is visually identical
   * to a card whose price failed to load, so the one result that most needed
   * explaining was the one that said the least. The search API now returns
   * `priceOnRequest: true` on such a result and the helper reads it, so the
   * label here is the same string the storefront, the product page, the share
   * text and the OG card use.
   *
   * An unpriced result still renders nothing, deliberately: this grid mixes
   * products with restaurants, shops and categories, and most result types have
   * no price concept at all. "Price unavailable" on a restaurant card would be
   * worse than the silence it replaced.
   */
  const searchPriceLabel = (item) => {
    if (isQuoteOnly(item)) return PRICE_ON_REQUEST_LABEL;
    const value = publishedPrice(item);
    return value === null ? null : formatCurrency(value);
  };

  const filteredResults = getFilteredResults();

  /* Delivery coverage on every result that belongs to a delivering vendor, in
     one batch. Search was the one browse surface without it, so a shopper could
     open a shop that does not deliver to them and only find out on its page.
     Same backend verdict as the listings, the shop banner and order-create. */
  const resultVendorIds = useMemo(
    () => filteredResults.map((item) => resultTargetOf(item)?.vendorId).filter(Boolean).map(String),
    [filteredResults]
  );
  const { byVendorId: coverageByVendor } = useCoverageBatch({
    vendorIds: resultVendorIds,
    enabled: resultVendorIds.length > 0,
  });

  return (
    <Container>
      <SEOHead
        title={searchQuery ? `"${searchQuery}" — Search Results` : 'Search'}
        description={hasSearched
          ? `${totalCount} results for "${searchQuery}" on Lusuku ${COUNTRY_NAME}'s online marketplace.`
          : 'Search for food, groceries, medicines and more on Lusuku.'}
        canonicalPath={`/search?q=${encodeURIComponent(searchQuery)}`}
      />
      <Navbar />
      <Content>
        <SearchHeader>
          <SearchHeaderRow>
            <div>
              <SearchTitle>
                {searchQuery
                  ? `Search results for "${searchQuery}"`
                  : "Search Results"}
              </SearchTitle>
              {hasSearched && (
                <ResultCount>
                  {totalCount} {totalCount === 1 ? "result" : "results"} found
                </ResultCount>
              )}
            </div>
            {hasSearched && searchQuery && (
              <ClearSearchButton onClick={() => {
                dispatch(clearSearch());
                history.push("/");
              }}>
                ✕ Clear Search
              </ClearSearchButton>
            )}
          </SearchHeaderRow>
        </SearchHeader>

        {categories.length > 1 && (
          <CategoryTabs>
            {categories.map((cat) => (
              <CategoryTab
                key={cat.key}
                active={activeCategory === cat.key}
                onClick={() => setActiveCategory(cat.key)}
              >
                {cat.label}
                <span>({cat.count})</span>
              </CategoryTab>
            ))}
          </CategoryTabs>
        )}

        {isSearching ? (
          <LoadingContainer>
            <Spinner />
          </LoadingContainer>
        ) : filteredResults.length > 0 ? (
          <ResultsGrid>
            {filteredResults.map((item, index) => {
              const PlaceholderIconComponent = getPlaceholderIcon(item.type);
              const target = resultTargetOf(item);
              const vendorId = target?.vendorId;
              const priceRow = searchPriceLabel(item);
              // The result's page as a real href, for search engines and new
              // tabs. A plain tap still runs handleResultClick.
              const withLink = (children, style) =>
                target?.path ? (
                  <CrawlLink
                    to={target.path}
                    onNavigate={() => handleResultClick(item)}
                    style={style}
                  >
                    {children}
                  </CrawlLink>
                ) : (
                  children
                );
              return (
              <ResultCard key={`${item._id}-${index}`} onClick={() => handleResultClick(item)}>
                {withLink(
                  <CardImage src={item.image} $contain={LOGO_RESULT_TYPES.includes(item.type) && !item.imageIsCover}>
                    {!item.image && (
                      <PlaceholderSurface>
                        <PlaceholderIconWrap>
                          <PlaceholderIconComponent />
                        </PlaceholderIconWrap>
                      </PlaceholderSurface>
                    )}
                  </CardImage>,
                  { display: "block" },
                )}
                <CardContent>
                  <CardType type={item.type}>{getTypeLabel(item.type)}</CardType>
                  <CardTitle>{withLink(item.displayName)}</CardTitle>
                  <CardSubtitle>{item.subtitle}</CardSubtitle>
                  {priceRow && <CardPrice>{priceRow}</CardPrice>}
                  {vendorId && (
                    <CardBadgeRow>
                      <DeliveryBadge
                        vendor={item}
                        coverage={coverageByVendor[String(vendorId)]}
                        size="small"
                      />
                    </CardBadgeRow>
                  )}
                </CardContent>
              </ResultCard>
              );
            })}
          </ResultsGrid>
        ) : hasSearched ? (
          <NoResults>
            <h3>No results found</h3>
            <p>Try searching with different keywords</p>
          </NoResults>
        ) : (
          <NoResults>
            <h3>Start searching</h3>
            <p>Enter a search term to find products, restaurants, and more</p>
          </NoResults>
        )}
      </Content>
      {/* <Footer /> */}
    </Container>
  );
};

export default SearchResultsPage;
